/**
 * Camera capability matrix.
 *
 * Mirrors `backend/app/models/commands.py`. Silently emitting a token the
 * camera will ignore is the single most common way these codes disappoint
 * people, so every feature the engine can reach for is declared here and gated
 * before it reaches the payload.
 */

export const CAMERAS = [
  "HERO9",
  "HERO10",
  "HERO11",
  "HERO12",
  "HERO13",
  "MAX",
  "MAX2",
  "MISSION",
] as const;

export type Camera = (typeof CAMERAS)[number];

/** Cameras the builder offers. HERO9/MAX/MISSION exist in the matrix so a
 *  shared preset built elsewhere still gates correctly, but they are not worth
 *  a chip in the UI. */
export const SELECTABLE_CAMERAS: readonly Camera[] = [
  "HERO13",
  "HERO12",
  "HERO11",
  "HERO10",
  "MAX2",
];

export const CAMERA_LABELS: Record<Camera, string> = {
  HERO9: "H9",
  HERO10: "H10",
  HERO11: "H11",
  HERO12: "H12",
  HERO13: "H13",
  MAX: "MAX",
  MAX2: "MAX2",
  MISSION: "Mission",
};

/** FourCC extensions, plus the pseudo-features the engine gates on. */
export type Feature =
  | "DIVE"
  | "WBDV"
  | "WARM"
  | "COOL"
  | "WBLK"
  | "WIDE"
  | "EXPX"
  | "EXPN"
  | "GCMP"
  | "BITR"
  | "EXPS"
  | "HIST"
  | "PRES"
  | "QRDR"
  | "DAMP"
  | "LEVL"
  /** The `x` exposure-compensation command — not a FourCC, so lowercase to
   *  match the other pseudo-features below rather than pretend it's one. */
  | "ev"
  | "tenbit"
  /** The 8:7 sensor readout that gives 4K 8:7 (r4X) and 5.3K 8:7 (r5X). */
  | "sensor87"
  /** HERO10's tall sensor readout, the only body with a 5.3K 4:3 code (r5T) —
   *  HERO11+ moved that sensor real estate to the 8:7 mode instead. */
  | "sensorTallH10";

const HERO_MODERN: readonly Camera[] = ["HERO11", "HERO12", "HERO13"];
const H10_PLUS: readonly Camera[] = ["HERO10", ...HERO_MODERN, "MAX", "MAX2"];

export const CAPABILITIES: Record<Feature, readonly Camera[]> = {
  DIVE: ["HERO12", "HERO13"],
  WBDV: ["HERO13"],
  WARM: ["HERO12", "HERO13", "MAX", "MAX2"],
  COOL: ["HERO12", "HERO13"],
  WBLK: ["HERO11", "HERO12", "HERO13", "MAX2"],
  WIDE: ["HERO11", "HERO12", "HERO13", "MAX2"],
  EXPX: H10_PLUS,
  EXPN: H10_PLUS,
  GCMP: [...HERO_MODERN, "MAX", "MAX2"],
  BITR: H10_PLUS,
  EXPS: [...HERO_MODERN, "MAX", "MAX2"],
  HIST: CAMERAS,
  PRES: ["HERO12", "HERO13", "MAX2"],
  QRDR: CAMERAS,
  DAMP: H10_PLUS,
  LEVL: [...HERO_MODERN, "MAX", "MAX2"],
  // GoPro's own Labs reference documents no per-camera restriction for `x`.
  ev: CAMERAS,
  tenbit: ["HERO11", "HERO12", "HERO13", "MAX2", "MISSION"],
  sensor87: HERO_MODERN,
  sensorTallH10: ["HERO10"],
};

export function supports(camera: Camera, feature: Feature): boolean {
  return CAPABILITIES[feature].includes(camera);
}

/** Human list for the "your camera will ignore this" warning. */
export function supportedBy(feature: Feature): string {
  return [...CAPABILITIES[feature]].sort().join(", ");
}
